import type { FetchListParams } from 'Services/ProductManagement/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC, SORT_ORDER_DESC } from 'Util/constants'
import { useState } from 'react'

interface UseListFunctionsProps {
  searchQueryParams: FetchListParams
  setSearchQueryParams: React.Dispatch<React.SetStateAction<FetchListParams>>
}

interface UseListFunctionsReturn {
  searchText: string
  handleChangePage: (event: unknown, newPage: number) => void
  handleRequestSort: (event: React.MouseEvent<unknown, MouseEvent>, property: string) => void
  handleSearchTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleSearch: () => void
  handlePTAChange: (pta: string | undefined) => void
  handleFilterChange: (filter: string | undefined) => void
}

/**
 * Custom hook that provides common list functionality for tables.
 *
 * This hook encapsulates the most frequently used list operations including:
 * - Search functionality with local state management
 * - Pagination with automatic start_index calculation
 * - Sorting with ascending/descending toggle logic
 * - Filtering by PTA
 *
 * @param searchQueryParams - Current search and filter parameters
 * @param setSearchQueryParams - State setter for updating search parameters
 * @returns Object containing search text state and all list operation handlers
 */
export function useListFunctions({
  searchQueryParams,
  setSearchQueryParams
}: UseListFunctionsProps): UseListFunctionsReturn {
  const [searchText, setSearchText] = useState(searchQueryParams.search_text ?? '')

  const handleChangePage = (_event: unknown, newPage: number) => {
    const newParams = {
      ...searchQueryParams,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }
    setSearchQueryParams(newParams)
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown, MouseEvent>, property: string) => {
    const isAsc = searchQueryParams.sort_order === SORT_ORDER_ASC
    const newParams = {
      ...searchQueryParams,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }
    setSearchQueryParams(newParams)
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setSearchQueryParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handlePTAChange = (pta: string | undefined) => {
    setSearchQueryParams(prevState => ({
      ...prevState,
      pta,
      start_index: undefined
    }))
  }

  const handleFilterChange = (filter: string | undefined) => {
    setSearchQueryParams(prevState => ({
      ...prevState,
      filter,
      start_index: undefined
    }))
  }

  return {
    searchText,
    handleChangePage,
    handleRequestSort,
    handleSearchTextChange,
    handleSearch,
    handlePTAChange,
    handleFilterChange
  }
}

export default useListFunctions
